import {Validators} from '@angular/forms';

import {Helper} from '../../app/shared/classes';
import {ColorCodesEnum, ComponentTypesEnum} from '../../app/shared/enums/common';
import {AutocompleteControl, BaseControl, RadioGroupControl, SelectControl, SlideToggleControl} from '../../app/shared/classes/controls';

import {TradingGroupStatusesEnum, TradingPlanTypeEnum} from '@spa/api';

import {
    ArrayEveryValidator,
    InArrayValidator,
    LatinDigitsValidator,
    OnlyDigitsValidator,
    OnlyLatinValidator,
    WhitespaceValidator
} from '@spa/validators';

import {
    TradingGroupClientTypesEnum,
    TradingGroupIsDefaultTypesEnum,
    TradingGroupLevelsEnum
} from '../../app/entries/panel/enums';
import { AutocompleteComponent, SelectOptionInterface } from 'appShared/components';

export const TradingGroupNameSelector = 'tradingGroupName';
export const TradingGroupAliasSelector = 'tradingGroupAlias';
export const TradingGroupStatusSelector = 'tradingGroupStatus';
export const TradingGroupClientTypeSelector = 'tradingGroupClientType';
export const TradingGroupFirmLevelSelector = 'tradingFirmLevelIdentifier';
export const TradingGroupAccountIdentifierSelector = 'tradingGroupAccountIdentifier';
export const TradingGroupAccountIsDefaultSelector = 'tradingGroupIsDefault';
export const TradingGroupAccountClientIdSelector = 'tradingGroupClientId';
export const TradingGroupAccountClientNameSelector = 'tradingGroupClientName';
export const TradingGroupIsDemoSelector = 'tradingGroupIsDemo';
export const TradingGroupPlanTypeSelector = 'tradingGroupPlanType';
export const TradingGroupPlanIdSelector = 'tradingGroupPlanId';
export const TradingGroupOmsGroupIdSelector = 'tradingGroupOmsGroupId';

export const TradingAccountIdentifierLength = 3;
export const TradingGroupAliasMaxLength = 15;

export interface TradingGroupControlsInterface {
    name: BaseControl;
    alias: BaseControl;
    status: SelectControl;
    clientType: SelectControl;
    firmLevel: SelectControl;
    accountIdentifier: BaseControl;
    isDefault: RadioGroupControl;
    clientId: BaseControl;
    clientName: BaseControl;
    isDemo: SlideToggleControl;
    planType: SelectControl,
    tradingPlanId: AutocompleteControl,
    omsGroupId: BaseControl
}

export const TradingGroupsControls = (): TradingGroupControlsInterface => {
    return {
        name: new BaseControl({
            name: TradingGroupNameSelector,
            value: null,
            placeholder: 'Name',
            validators: [WhitespaceValidator(), LatinDigitsValidator()],
            componentType: ComponentTypesEnum.Text,
            sortIndex: 1,
            floatingLabelNever: true
        }),
        alias: new BaseControl({
            name: TradingGroupAliasSelector,
            value: null,
            placeholder: 'Alias',
            validators: [WhitespaceValidator(), LatinDigitsValidator(), Validators.maxLength(TradingGroupAliasMaxLength)],
            componentType: ComponentTypesEnum.Text,
            sortIndex: 2,
            floatingLabelNever: true
        }),
        status: new SelectControl({
            name: TradingGroupStatusSelector,
            value: null,
            placeholder: 'Status',
            validators: [Validators.required, InArrayValidator([
                TradingGroupStatusesEnum.Active,
                TradingGroupStatusesEnum.Inactive
            ])],
            componentType: ComponentTypesEnum.Select,
            options: [
                {
                    label: Helper.textsHelper().getTradingGroupStatusV2Name(TradingGroupStatusesEnum.Active),
                    value: TradingGroupStatusesEnum.Active
                },
                {
                    label: Helper.textsHelper().getTradingGroupStatusV2Name(TradingGroupStatusesEnum.Inactive),
                    value: TradingGroupStatusesEnum.Inactive
                }
            ],
            sortIndex: 3,
            floatingLabelNever: true
        }),
        clientType: new SelectControl({
            name: TradingGroupClientTypeSelector,
            value: null,
            placeholder: 'Client Type',
            validators: [Validators.required, InArrayValidator([
                TradingGroupClientTypesEnum.Individual,
                TradingGroupClientTypesEnum.Company
            ])],
            componentType: ComponentTypesEnum.Select,
            options: [
                {
                    label: Helper.textsHelper().getTradingGroupClientType(TradingGroupClientTypesEnum.Individual),
                    value: TradingGroupClientTypesEnum.Individual
                },
                {
                    label: Helper.textsHelper().getTradingGroupClientType(TradingGroupClientTypesEnum.Company),
                    value: TradingGroupClientTypesEnum.Company
                }
            ],
            sortIndex: 4,
            floatingLabelNever: true
        }),
        firmLevel: new SelectControl({
            name: TradingGroupFirmLevelSelector,
            value: TradingGroupLevelsEnum.Tenant,
            placeholder: 'Firm Level',
            validators: [Validators.required, InArrayValidator([
                TradingGroupLevelsEnum.Tenant
            ])],
            componentType: ComponentTypesEnum.Select,
            options: [
                {
                    label: Helper.textsHelper().getTradingGroupLevelName(TradingGroupLevelsEnum.Tenant),
                    value: TradingGroupLevelsEnum.Tenant
                }
            ],
            readonly: true,
            sortIndex: 5,
            floatingLabelNever: true
        }),
        accountIdentifier: new BaseControl({
            name: TradingGroupAccountIdentifierSelector,
            value: null,
            placeholder: 'Identifier',
            validators: [
                WhitespaceValidator(),
                OnlyLatinValidator(),
                Validators.maxLength(TradingAccountIdentifierLength),
                Validators.minLength(TradingAccountIdentifierLength)
            ],
            componentType: ComponentTypesEnum.Text,
            sortIndex: 5,
            floatingLabelNever: true
        }),
        isDefault: new RadioGroupControl({
            name: TradingGroupAccountIsDefaultSelector,
            value: TradingGroupIsDefaultTypesEnum.Yes,
            placeholder: '',
            validators: [
                InArrayValidator([
                    TradingGroupIsDefaultTypesEnum.No,
                    TradingGroupIsDefaultTypesEnum.Yes
                ])
            ],
            componentType: ComponentTypesEnum.RadioGroup,
            invalid: false,
            errors: [],
            options: [
                {
                    label: Helper.textsHelper().getTradingGroupsIsDefaultText(TradingGroupIsDefaultTypesEnum.No),
                    value: TradingGroupIsDefaultTypesEnum.No,
                    color: ColorCodesEnum.Primary
                },
                {
                    label: Helper.textsHelper().getTradingGroupsIsDefaultText(TradingGroupIsDefaultTypesEnum.Yes),
                    value: TradingGroupIsDefaultTypesEnum.Yes,
                    color: ColorCodesEnum.Primary
                }
            ],
            sortIndex: 6
        }),
        clientId: new BaseControl({
            name: TradingGroupAccountClientIdSelector,
            value: null,
            placeholder: 'Client Id',
            validators: [OnlyDigitsValidator()],
            componentType: ComponentTypesEnum.Text,
            sortIndex: 1,
            readonly: true,
            disabled: true,
            floatingLabelNever: true
        }),
        clientName: new BaseControl({
            name: TradingGroupAccountClientNameSelector,
            value: null,
            placeholder: 'Client Name',
            validators: [],
            componentType: ComponentTypesEnum.Text,
            sortIndex: 7,
            readonly: true,
            disabled: true,
            floatingLabelNever: true
        }),
        isDemo: new SlideToggleControl({
            name: TradingGroupIsDemoSelector,
            value: false,
            checked: false,
            placeholder: 'Demo',
            validators: [Validators.required],
            componentType: ComponentTypesEnum.SlideToggle,
            expectBooleanValue: true,
            color: ColorCodesEnum.Primary,
            sortIndex: 8,
            readonly: true,
        }),
        planType: new SelectControl({
            name: TradingGroupPlanTypeSelector,
            value: null,
            placeholder: 'Trading Plan Types',
            validators: [ArrayEveryValidator(Helper.enumsHelper().getTradingPlanTypeAsOptions(false, [TradingPlanTypeEnum.All]).map(x =>  x.value as number))],
            componentType: ComponentTypesEnum.Select,
            options: Helper.enumsHelper().getTradingPlanTypeAsOptions(false, [TradingPlanTypeEnum.All]).map(x => x as SelectOptionInterface),
            readonly: true,
            sortIndex: 9,
            floatingLabelNever: true,
            multiple: true
        }),
        tradingPlanId: new AutocompleteControl({
            name: TradingGroupPlanIdSelector,
            value: null,
            placeholder: 'Trading Plan',
            validators: [],
            componentType: ComponentTypesEnum.Autocomplete,
            options: [],
            async: true,
            floatingLabelNever: true
        }),
        omsGroupId: new BaseControl({
            name: TradingGroupOmsGroupIdSelector,
            value: null,
            placeholder: 'Oms Group Id',
            validators: [Validators.maxLength(TradingGroupAliasMaxLength)],
            componentType: ComponentTypesEnum.Text,
            sortIndex: 2,
            floatingLabelNever: true
        }),

    };
};
